import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2, Users, TrendingUp, Zap,
  Activity, PieChart, History, ShieldAlert,
  Database, Bot, Layers, Server, RefreshCw, X,
} from 'lucide-react';
import { overviewService } from '../../../../services/admin/overviewService.js';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTokens(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatRevenue(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildChartData(rawArray) {
  if (!Array.isArray(rawArray) || rawArray.length === 0) {
    return { bars: [], labels: [], raw: [] };
  }
  const maxVal = Math.max(...rawArray.map(d => d.tokenUsedInDay), 1);
  const bars   = rawArray.map(d => Math.round((d.tokenUsedInDay / maxVal) * 100));
  const labels = rawArray.map(d => formatDate(d.dateOfTokenUsage));
  const raw    = rawArray.map(d => d.tokenUsedInDay);
  return { bars, labels, raw };
}

function buildIndustries(rawArray, topN = 4) {
  if (!Array.isArray(rawArray) || rawArray.length === 0) return [];
  const palette = ['bg-[#1d57a5]', 'bg-[#36a852]', 'bg-amber-500', 'bg-purple-500'];
  const sorted  = [...rawArray].sort((a, b) => b.count - a.count);
  const top     = sorted.slice(0, topN);
  const rest    = sorted.slice(topN);
  const totalCount = rawArray.reduce((s, d) => s + d.count, 0);
  const result = top.map((d, i) => ({
    name:  d.industryName,
    value: Math.round((d.count / totalCount) * 100),
    color: palette[i] ?? 'bg-slate-400',
  }));
  if (rest.length > 0) {
    const othersCount = rest.reduce((s, d) => s + d.count, 0);
    result.push({
      name:  'Others',
      value: Math.round((othersCount / totalCount) * 100),
      color: 'bg-slate-400',
    });
  }
  return result;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DashboardView() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ref for the scrollable chart container
  const chartScrollRef = useRef(null);

  const handleDownloadReport = async () => {
    try {
      setDownloading(true);
      const blob = await overviewService.downloadTenantDemographicsReport();
      downloadBlob(blob, `Tenant_Demographics_Report_${Date.now()}.pdf`);
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await overviewService.getOverviewData();
      setData(response?.data ?? response);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Chart data (derived from state — safe to compute before early returns) ──
  const tokenUsageRaw = data?.platformTokenUsage?.data ?? [];
  const { bars, labels, raw } = buildChartData(tokenUsageRaw);

  // ── Scroll to rightmost bar once bars are available ────────────────────────
  // Must be declared here (before any early returns) to satisfy Rules of Hooks.
  useEffect(() => {
    if (chartScrollRef.current && bars.length > 0) {
      requestAnimationFrame(() => {
        if (chartScrollRef.current) {
          chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
        }
      });
    }
  }, [bars.length]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-10 h-10 border-4 border-[#1d57a5]/20 border-t-[#1d57a5] rounded-full animate-spin" />
        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Loading dashboard…</p>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-500 flex items-center justify-center">
          <ShieldAlert size={24} />
        </div>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Failed to load dashboard</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs">{error}</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-[#1d57a5] text-white text-sm font-semibold rounded-full hover:bg-[#164280] transition-colors"
        >
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  // ── Destructure API data ───────────────────────────────────────────────────
  const companies   = data?.nofCompaniesOverview?.data;
  const users       = data?.nofPlatformUsersOverview?.data;
  const mrr         = data?.mRROverview?.data;
  const tokens      = data?.tokensOverview?.data;
  const demographics= data?.tenantDemographicsOverview?.data ?? [];
  const workspace   = data?.workspaceCreatedRecently?.data;
  const dbSynced    = data?.databaseSyncedRecently?.data;
  const tenant      = data?.tenantOnboardedRecently?.data;
  const modelUpdate = data?.modelUpdates?.data;

  // ── Metric Cards ───────────────────────────────────────────────────────────
  const metrics = [
    {
      label: 'Enterprise Clients',
      value: companies?.numberOfCompanies  != null ? String(companies.numberOfCompanies)            : '—',
      trend: companies?.netChangeThisMonth != null ? `+${companies.netChangeThisMonth} this month`  : 'No change data',
      color: 'text-[#1d57a5]',
      bg:    'bg-[#1d57a5]/10',
      icon:  Building2,
    },
    {
      label: 'Global Platform Users',
      value: users?.totalPlatformUsers     != null ? String(users.totalPlatformUsers)               : '—',
      trend: users?.percentChangeThisWeek  != null ? `+${users.percentChangeThisWeek}% vs last week`: 'No change data',
      color: 'text-[#36a852]',
      bg:    'bg-[#36a852]/10',
      icon:  Users,
    },
    {
      label: 'Platform MRR',
      value: mrr?.totalRevenue             != null ? formatRevenue(mrr.totalRevenue)                : '—',
      trend: mrr?.percentageGrowthChange   != null ? `+${mrr.percentageGrowthChange}% growth`       : 'No change data',
      color: 'text-amber-500',
      bg:    'bg-amber-500/10',
      icon:  TrendingUp,
    },
    {
      label: 'Tokens Processed',
      value: tokens?.tokensUsedIn30Days    != null ? formatTokens(tokens.tokensUsedIn30Days)        : '—',
      trend: 'In the last 30 days',
      color: 'text-purple-500',
      bg:    'bg-purple-500/10',
      icon:  Zap,
    },
  ];

  // ── Industry data ──────────────────────────────────────────────────────────
  const industries = buildIndustries(demographics, 4);

  // ── Recent Events ──────────────────────────────────────────────────────────
  const recentEvents = [
    workspace && {
      icon:  Layers,
      color: 'text-purple-500',
      bg:    'bg-purple-100 dark:bg-purple-900/30',
      title: 'New Workspace Created',
      desc:  `${workspace.companyName} initialized "${workspace.workspaceName}".`,
      time:  workspace.timeElapsed,
    },
    dbSynced && {
      icon:  Database,
      color: 'text-amber-500',
      bg:    'bg-amber-100 dark:bg-amber-900/30',
      title: 'Database Synced',
      desc:  `${dbSynced.companyName} schema indexing completed successfully.`,
      time:  dbSynced.timeElapsed,
    },
    tenant && {
      icon:  Building2,
      color: 'text-[#1d57a5]',
      bg:    'bg-[#1d57a5]/10 dark:bg-[#4a8ce3]/20',
      title: 'New Tenant Onboarded',
      desc:  `${tenant.companyName} joined the platform.`,
      time:  tenant.timeElapsed,
    },
    modelUpdate && {
      icon:  Bot,
      color: 'text-[#36a852]',
      bg:    'bg-green-100 dark:bg-green-900/30',
      title: 'Model Updated',
      desc:  `${modelUpdate.modelName ?? 'A model'} was updated to ${modelUpdate.version ?? 'the latest version'}.`,
      time:  modelUpdate.timeElapsed,
    },
  ].filter(Boolean);

  // ── Bar width & gap constants ──────────────────────────────────────────────
  const BAR_W = 32;   // px — width of each bar column
  const BAR_GAP = 6;  // px — gap between bars (matches gap-[6px])

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* ── Row 1: Metric Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="bg-white dark:bg-slate-800 p-4 rounded-[1.5rem] shadow-sm border border-slate-100 dark:border-slate-700 relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="relative z-10">
                <div className={`w-10 h-10 rounded-xl ${stat.bg} ${stat.color} flex items-center justify-center mb-3`}>
                  <Icon size={20} />
                </div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{stat.value}</p>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2">{stat.label}</h3>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">{stat.trend}</p>
              </div>
              <div className={`absolute -bottom-6 -right-6 w-32 h-32 rounded-full ${stat.bg} opacity-50 blur-2xl group-hover:scale-150 transition-transform duration-500 pointer-events-none`} />
            </div>
          );
        })}
      </div>

      {/* ── Row 2: Token Chart + Tenant Demographics ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Bar Chart — Token Usage */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 p-5 flex flex-col">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <Activity size={20} className="text-[#1d57a5] dark:text-[#4a8ce3]" /> Platform Token Usage
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Daily aggregated tokens across all LLM models</p>
            </div>
          </div>

          {bars.length === 0 ? (
            <div className="flex-1 min-h-[9.5rem] flex items-center justify-center">
              <p className="text-sm text-slate-400">No token usage data available.</p>
            </div>
          ) : (
            /*
             * Layout:  [ Y-axis fixed col (52px) ] [ scrollable bars col (flex-1) ]
             *
             * The Y-axis labels + horizontal grid lines live in the LEFT column and
             * never move.  Only the bars + x-axis labels live in the RIGHT scroll
             * container.  This prevents the "labels scroll with bars" bug.
             */
            <div className="flex mt-2" style={{ height: '220px' }}>

              {/* ── LEFT: fixed Y-axis column ── */}
              <div className="flex flex-col flex-shrink-0" style={{ width: '52px' }}>
                {/* Top section: Y labels aligned to grid rows — occupies same height as bars area */}
                <div className="flex-1 flex flex-col justify-between pb-0">
                  {[100, 75, 50, 25, 0].map((line) => (
                    <span
                      key={line}
                      className="text-[10px] text-slate-400 dark:text-slate-500 leading-none text-right pr-3 block"
                    >
                      {line}%
                    </span>
                  ))}
                </div>
                {/* Bottom spacer matches X-axis label row height (28px) */}
                <div style={{ height: '28px' }} />
              </div>

              {/* ── RIGHT: chart body (grid lines fixed + scrollable bars on top) ── */}
              <div className="flex-1 flex flex-col min-w-0 relative">

                {/* Fixed grid lines — these stay put, never scroll */}
                <div className="flex-1 relative">
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    {[100, 75, 50, 25, 0].map((line) => (
                      <div
                        key={line}
                        className="w-full border-t border-dashed border-slate-200 dark:border-slate-700"
                      />
                    ))}
                  </div>

                  {/* Scrollable bars layer — sits on top of the fixed grid */}
                  <div
                    ref={chartScrollRef}
                    className="absolute inset-0 overflow-x-auto cursor-grab active:cursor-grabbing"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    {/* Inner width = exactly bars × (barW + gap), no trailing space */}
                    <div
                      style={{
                        width: `${bars.length * (BAR_W + BAR_GAP)}px`,
                        minWidth: '100%',
                        gap: `${BAR_GAP}px`,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                      }}
                    >
                      {bars.map((val, i) => {
                        const isLatest = i === bars.length - 1;
                        return (
                          <div
                            key={i}
                            className="relative flex flex-col items-center justify-end h-full group"
                            style={{ width: `${BAR_W}px`, flexShrink: 0 }}
                          >
                            {/* Hover tooltip */}
                            <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-[10px] bg-slate-800 dark:bg-slate-900 text-white px-2 py-1 rounded-md shadow whitespace-nowrap pointer-events-none z-30">
                              {formatTokens(raw[i])}
                            </div>

                            {/* Bar */}
                            <div
                              className={`rounded-t-md transition-all duration-300 ${
                                isLatest
                                  ? 'bg-[#1d57a5] hover:bg-[#164280]'
                                  : 'bg-[#378ADD] hover:bg-[#1d57a5]'
                              }`}
                              style={{
                                width:  `${BAR_W}px`,
                                height: `${Math.max(val, 2)}%`,
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* X-axis labels — inside a second scroll container synced to bars */}
                <div
                  className="overflow-x-auto flex-shrink-0"
                  style={{ height: '28px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  ref={(el) => {
                    // Keep x-label scroller in sync with bar scroller
                    if (!el) return;
                    const barScroll = chartScrollRef.current;
                    if (!barScroll) return;
                    const onScroll = () => { el.scrollLeft = barScroll.scrollLeft; };
                    barScroll.addEventListener('scroll', onScroll, { passive: true });
                  }}
                >
                  <div
                    style={{
                      width: `${bars.length * (BAR_W + BAR_GAP)}px`,
                      minWidth: '100%',
                      display: 'flex',
                      gap: `${BAR_GAP}px`,
                      paddingTop: '6px',
                    }}
                  >
                    {labels.map((label, i) => (
                      <div
                        key={i}
                        className="text-center text-[10px] text-slate-500 dark:text-slate-400 whitespace-nowrap"
                        style={{ width: `${BAR_W}px`, flexShrink: 0 }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>

        {/* Tenant Demographics */}
        <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 p-5">
          <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-1">
            <PieChart size={20} className="text-amber-500" /> Tenant Demographics
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Client distribution by industry</p>

          {industries.length === 0 ? (
            <p className="text-sm text-slate-400 text-center mt-8">No industry data available.</p>
          ) : (
            <div className="space-y-3">
              {industries.map(ind => (
                <div key={ind.name} className="group cursor-pointer">
                  <div className="flex justify-between items-end text-sm mb-1.5">
                    <span
                      className="font-bold text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors truncate max-w-[75%]"
                      title={ind.name}
                    >
                      {ind.name}
                    </span>
                    <span className="text-slate-500 font-mono text-xs font-semibold">{ind.value}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${ind.color} rounded-full group-hover:brightness-110 transition-all`}
                      style={{ width: `${ind.value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
            <button
              onClick={handleDownloadReport}
              disabled={downloading}
              className="w-full py-2.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900/50 dark:hover:bg-slate-900 text-slate-600 dark:text-slate-300 text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading ? 'Downloading…' : 'Download Full Report'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Row 3: Activity Feed + System Health ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Recent Events */}
        <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <History size={20} className="text-[#36a852]" /> Recent Events
            </h3>
            <button
              onClick={() => setShowAllEvents(true)}
              className="text-sm font-medium text-[#1d57a5] dark:text-[#4a8ce3] hover:underline"
            >
              View All
            </button>
          </div>

          {recentEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <History size={32} className="text-slate-200 dark:text-slate-700" />
              <p className="text-sm text-slate-400">No recent events to display.</p>
            </div>
          ) : (
            <div className="relative border-l-2 border-slate-100 dark:border-slate-700 ml-3 pl-5 space-y-5">
              {recentEvents.slice(0, 3).map((a, i) => {
                const Icon = a.icon;
                return (
                  <div key={i} className="relative">
                    <div className={`absolute -left-[2.15rem] top-1 w-8 h-8 rounded-full ${a.bg} ${a.color} flex items-center justify-center border-4 border-white dark:border-slate-800`}>
                      <Icon size={12} strokeWidth={3} />
                    </div>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{a.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{a.desc}</p>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1.5">{a.time}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* System Health */}
        <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 p-5">
          <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
            <ShieldAlert size={20} className="text-red-500" /> System Health &amp; Anomalies
          </h3>
          <div className="space-y-3">

            <div className="flex gap-3 p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50">
              <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 flex items-center justify-center flex-shrink-0">
                <Activity size={18} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-amber-800 dark:text-amber-500 mb-1">API Latency Spike Detected</h4>
                <p className="text-xs text-amber-700/80 dark:text-amber-400/80">Anthropic endpoint experienced elevated response times (avg 4.2s) over the last 15 minutes.</p>
              </div>
            </div>

            <div className="flex gap-3 p-3 rounded-2xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50">
              <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 flex items-center justify-center flex-shrink-0">
                <Database size={18} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-red-800 dark:text-red-500 mb-1">Sync Failure: Stark Logistics</h4>
                <p className="text-xs text-red-700/80 dark:text-red-400/80">Database connection timeout. Scheduled retry in 5 minutes. Please verify credentials.</p>
              </div>
            </div>

            <div className="flex gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/30 border border-slate-100 dark:border-slate-700">
              <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 flex items-center justify-center flex-shrink-0">
                <Server size={18} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Vector DB Storage Check</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total capacity is currently at 45.2%. No immediate scaling required.</p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Recent Events Modal ── */}
      {showAllEvents && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAllEvents(false)} />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <History size={17} className="text-[#36a852]" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-800 dark:text-white">Recent Events</h3>
                  <p className="text-xs text-slate-500">{recentEvents.length} event{recentEvents.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button
                onClick={() => setShowAllEvents(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {recentEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 gap-2">
                  <History size={28} className="text-slate-200 dark:text-slate-700" />
                  <p className="text-sm text-slate-400">No recent events to display.</p>
                </div>
              ) : (
                <div className="relative border-l-2 border-slate-100 dark:border-slate-700 ml-3 pl-5 space-y-5">
                  {recentEvents.map((a, i) => {
                    const Icon = a.icon;
                    return (
                      <div key={i} className="relative">
                        <div className={`absolute -left-[2.15rem] top-1 w-8 h-8 rounded-full ${a.bg} ${a.color} flex items-center justify-center border-4 border-white dark:border-slate-800`}>
                          <Icon size={12} strokeWidth={3} />
                        </div>
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{a.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{a.desc}</p>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1.5">{a.time}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-700 flex justify-end flex-shrink-0">
              <button
                onClick={() => setShowAllEvents(false)}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-[#1d57a5] text-white hover:bg-[#164280] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
